import { useState } from 'react';
import { Instagram, Youtube, Linkedin, Facebook, Check, Loader2, ChevronLeft, ExternalLink } from 'lucide-react';
import { TikTokIcon } from '@/components/ui/TikTokIcon';
import { openMetricoolUrl } from '@/utils/metricoolNavigation';
import { useSocialNetworkConnect } from '@/hooks/useSocialNetworkConnect';
import type { Channel } from '@/services/business/calendar';

interface NetworkSidePanelProps {
  readonly disconnectedNetworks: string[];
  readonly onClose: () => void;
  readonly onNetworkConnected?: () => void;
  readonly onAllNetworksConnected?: () => void;
  readonly userId?: string; // For admin viewing another user
}

const networkConfig: Record<string, { label: string; icon: React.ComponentType<{ className?: string }> }> = {
  instagram: { label: 'Instagram', icon: Instagram },
  tiktok: { label: 'TikTok', icon: TikTokIcon },
  youtube: { label: 'YouTube', icon: Youtube },
  linkedin: { label: 'LinkedIn', icon: Linkedin },
  facebook: { label: 'Facebook', icon: Facebook },
};

export function NetworkSidePanel({ 
  disconnectedNetworks, 
  onClose, 
  onNetworkConnected, 
  onAllNetworksConnected,
  userId 
}: NetworkSidePanelProps) {
  const { connectNetwork, refreshConnections } = useSocialNetworkConnect();
  const [connectingNetworks, setConnectingNetworks] = useState<Set<string>>(new Set());
  const [connectedNetworks, setConnectedNetworks] = useState<Set<string>>(new Set());

  const handleConnect = async (network: string) => {
    setConnectingNetworks(prev => new Set([...prev, network]));

    const result = await connectNetwork(network as Channel, userId);

    if (result.success && result.loginUrl) {
      openMetricoolUrl(result.loginUrl);
      await refreshConnections(userId);
      const newConnectedNetworks = new Set([...connectedNetworks, network]);
      setConnectedNetworks(newConnectedNetworks);
      const remaining = disconnectedNetworks.filter(n => !newConnectedNetworks.has(n));
      if (remaining.length === 0) {
        onAllNetworksConnected?.();
      }
      onNetworkConnected?.();
    } else {
      console.error(`Failed to get connect URL for ${network}:`, result.error);
    }

    setConnectingNetworks(prev => {
      const next = new Set(prev);
      next.delete(network);
      return next;
    });
  };

  const remainingNetworks = disconnectedNetworks.filter(n => !connectedNetworks.has(n));

  return (
    <div className="h-full flex flex-col">
      {/* Header with back button */}
      <div className="px-6 py-5 border-b border-white/[0.04]">
        <button 
          onClick={onClose}
          className="flex items-center gap-2 text-white/60 hover:text-white/80 transition-colors"
        >
          <ChevronLeft className="h-5 w-5" />
          <span className="text-sm font-medium">Retour</span>
        </button>
      </div>

      {/* Title */}
      <div className="px-6 py-5">
        <h2 className="text-lg font-semibold text-white">Connecter vos réseaux</h2>
        <p className="text-sm text-white/50 mt-1">
          Connectez vos comptes pour programmer vos publications
        </p>
      </div>

      {/* Network buttons */}
      <div className="flex-1 px-6 space-y-3 overflow-y-auto">
        {remainingNetworks.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
            <div className="h-12 w-12 rounded-full bg-warning/10 flex items-center justify-center">
              <Check className="h-6 w-6 text-warning" />
            </div>
            <div>
              <p className="text-white font-medium">Tous les réseaux sont connectés</p>
              <p className="text-white/50 text-sm mt-1">Vous êtes prêt à publier !</p>
            </div>
          </div>
        ) : (
          remainingNetworks.map(network => {
            const config = networkConfig[network];
            if (!config) return null;
            
            const isConnecting = connectingNetworks.has(network);
            const IconComponent = config.icon;
            
            return (
              <button
                key={network}
                onClick={() => handleConnect(network)}
                disabled={isConnecting}
                className="w-full flex items-center justify-between px-4 py-4 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06] hover:border-warning/20 transition-all duration-200 disabled:opacity-50"
              >
                <div className="flex items-center gap-3">
                  <IconComponent className="h-5 w-5 text-white/70" />
                  <span className="text-sm font-medium text-white/80">{config.label}</span>
                </div>
                {isConnecting ? (
                  <Loader2 className="h-5 w-5 text-warning animate-spin" />
                ) : (
                  <ExternalLink className="h-4 w-4 text-white/40" />
                )}
              </button>
            );
          })
        )}
      </div>

      {/* Close button when all connected */}
      {remainingNetworks.length === 0 && (
        <div className="px-6 py-5 border-t border-white/[0.04]">
          <button
            onClick={onClose}
            className="w-full py-3 text-sm font-medium text-white/60 hover:text-white/80 transition-colors rounded-lg hover:bg-white/[0.04]"
          >
            Fermer
          </button>
        </div>
      )}
    </div>
  );
}
